import React, { useMemo } from 'react';
import { css } from '@emotion/react';
import { Link } from '@tanstack/react-router';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Accordion, Text } from '@frontend/design-system';
import { UserRole } from '../../utils/constants';
import { checkRbacAccess, formatString } from '../../utils/helpers';
import { SupportedFeature } from '../../utils/typings';

interface SupportedFeaturesAccordionProps {
  supportedFeatures?: SupportedFeature[];
  userRole?: keyof typeof UserRole;
}

const SupportedFeaturesAccordion: React.FC<SupportedFeaturesAccordionProps> = ({
  supportedFeatures = [],
  userRole,
}) => {
  const visibleFeatures = useMemo(
    () => supportedFeatures.filter((feature) => checkRbacAccess(feature, userRole) && feature.isSupported),
    [supportedFeatures, userRole]
  );

  return (
    <Accordion variant='main' showBoxShadow>
      <Accordion.Item value='supported-features'>
        <Accordion.Header>
          <Text weight='bold'>Supported Features</Text>
        </Accordion.Header>

        <Accordion.Body>
          {visibleFeatures.length === 0 ? (
            <Text>No supported features available.</Text>
          ) : (
            <Accordion variant='main' isMulti>
              {visibleFeatures.map((feature, index) => {
                const sections = Object.entries(feature)
                  .filter(([key, value]) => key !== 'dataDependencies' && typeof value === 'object' && value?.value)
                  .filter(([, section]) => checkRbacAccess(section, userRole));

                const hasContent = sections.length > 0;

                return (
                  <Accordion.Item key={feature.featureName} value={feature.featureName ?? `feature-${index}`}>
                    <Accordion.Header>
                      <Text weight='bold' css={textAlignStyle}>
                        <Icon name='confirm-badge' color='success' /> {feature.featureName}
                      </Text>
                    </Accordion.Header>

                    <Accordion.Body>
                      {!hasContent ? (
                        <Text css={textAlignStyle}>
                          <Icon name='confirm-badge' color='success' /> {feature.featureName} is a supported feature
                          with this integration.
                        </Text>
                      ) : (
                        sections.map(([key, section]) => {
                          const isLink = typeof section.value === 'string' && section.value.startsWith('http');

                          return (
                            <div key={key} css={featureItemStyle}>
                              <Text as='div' css={featureItemContentStyle}>
                                {formatString(key)}
                                {' : '}
                                {isLink ? (
                                  <Link to={section.value} target='_blank' rel='noopener noreferrer'>
                                    {section.value}
                                  </Link>
                                ) : (
                                  <span>{Array.isArray(section.value) ? section.value.join(', ') : section.value}</span>
                                )}
                              </Text>
                            </div>
                          );
                        })
                      )}
                    </Accordion.Body>
                  </Accordion.Item>
                );
              })}
            </Accordion>
          )}
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>
  );
};

export default SupportedFeaturesAccordion;

const featureItemStyle = css`
  display: flex;

  &::before {
    content: '•';
    color: #555;
    margin-right: ${theme.spacing(0.5)};
  }
`;

const featureItemContentStyle = css`
  margin-bottom: ${theme.spacing(1)};
`;

const textAlignStyle = css`
  display: flex;
  align-items: center;
  gap: ${theme.spacing(0.5)};
`;
